/**
 * Reporting period labels — mirrors novata-platform @novata/dates (without that package):
 *   packages/dates/src/utils/parse-reporting-period-date-and-frequency.ts
 *   packages/dates/src/format/get-reporting-period-label.ts
 *
 * Uses the same dayjs UTC + frequency start/label steps as the platform.
 */
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(quarterOfYear);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

/** Same as @novata/dates api() — dayjs instance for calendar period math. */
const api = (date, format) => dayjs(date, format);

const DEFAULT_FREQUENCY = "Annually";

const frequencyToStartDate = {
  Annually: (date) => date.startOf("year"),
  Semiannually: (date) => {
    const startOfSecondHalf = date.clone().startOf("year").add(6, "months");
    if (date.isBefore(startOfSecondHalf)) {
      return startOfSecondHalf.startOf("year");
    }
    return startOfSecondHalf;
  },
  Quarterly: (date) => date.startOf("quarter"),
  Monthly: (date) => date.startOf("month"),
};

const REPORTING_FREQUENCY_TO_DATE_LABELER = {
  Semiannually: (date) => `H${[1, 2].includes(api(date).quarter()) ? 1 : 2}`,
  Quarterly: (date) => api(date).format("[Q]Q"),
  Monthly: (date) => api(date).format("MMM"),
  Annually: () => "",
};

const isValidPeriodStart = (periodStart) => {
  if (!periodStart) {
    return false;
  }
  return api(new Date(periodStart)).isValid();
};

/**
 * @param {Date | string} periodStart
 * @param {"Monthly" | "Quarterly" | "Semiannually" | "Annually"} frequency
 * @param {boolean} [inputIsLocal=false]
 * @returns {{ start: import("dayjs").Dayjs, end: import("dayjs").Dayjs }}
 */
export const parseReportingPeriodDateAndFrequency = (
  periodStart,
  frequency,
  inputIsLocal = false
) => {
  if (!isValidPeriodStart(periodStart)) {
    throw new Error("Invalid time value");
  }

  const parsed = inputIsLocal ? api(periodStart) : api(periodStart).utc();
  const dateOnly = parsed.format("YYYY-MM-DD");

  if (!dateOnly) {
    throw new Error("Invalid date");
  }

  const calendarDate = api(dateOnly);

  return {
    start: frequencyToStartDate[frequency](calendarDate),
    end: calendarDate,
  };
};

/**
 * Platform getReportingPeriodLabel — same signature and output.
 */
export const getReportingPeriodLabel = (
  date,
  frequency = DEFAULT_FREQUENCY,
  inputIsLocal = false
) => {
  if (!date) {
    return "";
  }

  const freq = frequency || DEFAULT_FREQUENCY;
  const parsed = parseReportingPeriodDateAndFrequency(date, freq, inputIsLocal);
  const label = REPORTING_FREQUENCY_TO_DATE_LABELER[freq](parsed.start);
  return `${label && `${label} `}${parsed.start.year()}`;
};

/**
 * Excel add-in entry point: never throws; returns "" for invalid input.
 */
export const formatReportingPeriod = (reportingPeriodStart, reportingFrequency) => {
  try {
    return getReportingPeriodLabel(reportingPeriodStart, reportingFrequency);
  } catch {
    return "";
  }
};
